import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { useOperators } from './operators';
import { useMapGetter } from 'dashboard/composables/store.js';
import { useCompanyLabelsStore } from 'dashboard/stores/companyLabels';
import {
  buildAttributesFilterTypes,
  COMPANY_ATTRIBUTES,
} from './helper/filterHelper.js';

/**
 * Composable that provides company filtering context, used to build the
 * "Empresa" criteria section of an Audience (spec 137). Mirrors
 * useContactFilterContext, matching the fixed fields Companies::FilterService
 * exposes on the backend.
 * @returns {{ filterTypes: import('vue').ComputedRef<import('./contactProvider.js').FilterType[]> }}
 */
export function useCompanyFilterContext() {
  const { t } = useI18n();

  const companyAttributes = useMapGetter('attributes/getCompanyAttributes');
  const companyLabelsStore = useCompanyLabelsStore();

  const {
    equalityOperators,
    containmentOperators,
    comparisonOperators,
    dateOperators,
    getOperatorTypes,
  } = useOperators();

  const customFilterTypes = computed(() =>
    buildAttributesFilterTypes(
      companyAttributes.value,
      getOperatorTypes,
      'company'
    )
  );

  const filterTypes = computed(() => [
    {
      attributeKey: COMPANY_ATTRIBUTES.NAME,
      value: COMPANY_ATTRIBUTES.NAME,
      attributeName: t('AUDIENCES.FILTER.COMPANY.NAME'),
      label: t('AUDIENCES.FILTER.COMPANY.NAME'),
      inputType: 'plainText',
      dataType: 'text',
      filterOperators: containmentOperators.value,
      attributeModel: 'standard',
    },
    {
      attributeKey: COMPANY_ATTRIBUTES.DOMAIN,
      value: COMPANY_ATTRIBUTES.DOMAIN,
      attributeName: t('AUDIENCES.FILTER.COMPANY.DOMAIN'),
      label: t('AUDIENCES.FILTER.COMPANY.DOMAIN'),
      inputType: 'plainText',
      dataType: 'text',
      filterOperators: containmentOperators.value,
      attributeModel: 'standard',
    },
    {
      attributeKey: COMPANY_ATTRIBUTES.CNPJ,
      value: COMPANY_ATTRIBUTES.CNPJ,
      attributeName: t('AUDIENCES.FILTER.COMPANY.CNPJ'),
      label: t('AUDIENCES.FILTER.COMPANY.CNPJ'),
      inputType: 'plainText',
      dataType: 'text',
      filterOperators: equalityOperators.value,
      attributeModel: 'standard',
    },
    {
      attributeKey: COMPANY_ATTRIBUTES.EMAIL,
      value: COMPANY_ATTRIBUTES.EMAIL,
      attributeName: t('AUDIENCES.FILTER.COMPANY.EMAIL'),
      label: t('AUDIENCES.FILTER.COMPANY.EMAIL'),
      inputType: 'plainText',
      dataType: 'text',
      filterOperators: containmentOperators.value,
      attributeModel: 'standard',
    },
    {
      attributeKey: COMPANY_ATTRIBUTES.PHONE_NUMBER,
      value: COMPANY_ATTRIBUTES.PHONE_NUMBER,
      attributeName: t('AUDIENCES.FILTER.COMPANY.PHONE_NUMBER'),
      label: t('AUDIENCES.FILTER.COMPANY.PHONE_NUMBER'),
      inputType: 'plainText',
      dataType: 'text',
      filterOperators: containmentOperators.value,
      attributeModel: 'standard',
    },
    {
      attributeKey: COMPANY_ATTRIBUTES.CITY,
      value: COMPANY_ATTRIBUTES.CITY,
      attributeName: t('AUDIENCES.FILTER.COMPANY.CITY'),
      label: t('AUDIENCES.FILTER.COMPANY.CITY'),
      inputType: 'plainText',
      dataType: 'text',
      filterOperators: containmentOperators.value,
      attributeModel: 'standard',
    },
    {
      attributeKey: COMPANY_ATTRIBUTES.COUNTRY_CODE,
      value: COMPANY_ATTRIBUTES.COUNTRY_CODE,
      attributeName: t('AUDIENCES.FILTER.COMPANY.COUNTRY_CODE'),
      label: t('AUDIENCES.FILTER.COMPANY.COUNTRY_CODE'),
      inputType: 'plainText',
      dataType: 'text',
      filterOperators: equalityOperators.value,
      attributeModel: 'standard',
    },
    {
      attributeKey: COMPANY_ATTRIBUTES.CONTACTS_COUNT,
      value: COMPANY_ATTRIBUTES.CONTACTS_COUNT,
      attributeName: t('AUDIENCES.FILTER.COMPANY.CONTACTS_COUNT'),
      label: t('AUDIENCES.FILTER.COMPANY.CONTACTS_COUNT'),
      inputType: 'plainText',
      dataType: 'number',
      filterOperators: comparisonOperators.value,
      attributeModel: 'standard',
    },
    {
      attributeKey: COMPANY_ATTRIBUTES.CREATED_AT,
      value: COMPANY_ATTRIBUTES.CREATED_AT,
      attributeName: t('AUDIENCES.FILTER.COMPANY.CREATED_AT'),
      label: t('AUDIENCES.FILTER.COMPANY.CREATED_AT'),
      inputType: 'date',
      dataType: 'text',
      filterOperators: dateOperators.value,
      attributeModel: 'standard',
    },
    {
      attributeKey: COMPANY_ATTRIBUTES.LAST_ACTIVITY_AT,
      value: COMPANY_ATTRIBUTES.LAST_ACTIVITY_AT,
      attributeName: t('AUDIENCES.FILTER.COMPANY.LAST_ACTIVITY_AT'),
      label: t('AUDIENCES.FILTER.COMPANY.LAST_ACTIVITY_AT'),
      inputType: 'date',
      dataType: 'text',
      filterOperators: dateOperators.value,
      attributeModel: 'standard',
    },
    {
      attributeKey: COMPANY_ATTRIBUTES.LABELS,
      value: COMPANY_ATTRIBUTES.LABELS,
      attributeName: t('AUDIENCES.FILTER.COMPANY.LABELS'),
      label: t('AUDIENCES.FILTER.COMPANY.LABELS'),
      inputType: 'multiSelect',
      options: companyLabelsStore.getLabels.map(label => ({
        id: label.title,
        name: label.title,
      })),
      dataType: 'text',
      filterOperators: equalityOperators.value,
      attributeModel: 'standard',
    },
    ...customFilterTypes.value,
  ]);

  return { filterTypes };
}
